import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import {
  Folder as FolderIcon, FileText, FileSpreadsheet, FileImage, File as FileIcon,
  Plus, Upload as UploadIcon, Loader2, Download, Pencil, Trash2, X,
  ChevronRight, ChevronLeft, Search, Cloud, Tag, ArrowUp, ArrowDown, ArrowUpDown,
  Rows3, LayoutGrid, Check as CheckIcon, HardDrive,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── tokens (cream/black/yellow — matches the shared Document Library style) ──
const T = {
  page: '#fbfbf3', card: '#ffffff', border: '#ece8d8', head: '#f5f3e9', hover: '#f3efe1',
  ink: '#2a2a2a', ink2: '#5a564e', ink3: '#8c8779', ink4: '#b8b3a4',
  yellow: '#ffd87a', focus: '#f7c757', black: '#2a2a2a',
};
const stripes = `repeating-linear-gradient(135deg,${T.head} 0 8px,#fbfaf2 8px 16px)`;

const TYPE_META = {
  pdf:  { bg: '#fce8eb', text: '#8a1020', dot: '#e31837', label: 'PDF' },
  docx: { bg: '#dce8f4', text: '#1a4a7a', dot: '#3a7abf', label: 'DOCX' },
  doc:  { bg: '#dce8f4', text: '#1a4a7a', dot: '#3a7abf', label: 'DOC' },
  xlsx: { bg: '#e6ecd2', text: '#4a5a2a', dot: '#7e9344', label: 'XLSX' },
  xls:  { bg: '#e6ecd2', text: '#4a5a2a', dot: '#7e9344', label: 'XLS' },
  csv:  { bg: '#e6ecd2', text: '#4a5a2a', dot: '#7e9344', label: 'CSV' },
  pptx: { bg: '#fde9b3', text: '#7a5a17', dot: '#d9a522', label: 'PPTX' },
  ppt:  { bg: '#fde9b3', text: '#7a5a17', dot: '#d9a522', label: 'PPT' },
  png:  { bg: '#ede8f8', text: '#4a2a8a', dot: '#7c3aed', label: 'PNG' },
  jpg:  { bg: '#ede8f8', text: '#4a2a8a', dot: '#7c3aed', label: 'JPG' },
  jpeg: { bg: '#ede8f8', text: '#4a2a8a', dot: '#7c3aed', label: 'JPG' },
  gif:  { bg: '#ede8f8', text: '#4a2a8a', dot: '#7c3aed', label: 'GIF' },
  txt:  { bg: '#f5f3e9', text: '#5a564e', dot: '#b8b3a4', label: 'TXT' },
};
const TYPES = [
  { v: 'all', l: 'All types' }, { v: 'folder', l: 'Folders' }, { v: 'pdf', l: 'PDF' },
  { v: 'docx', l: 'Word' }, { v: 'xlsx', l: 'Excel' }, { v: 'csv', l: 'CSV' }, { v: 'png', l: 'Images' },
];
const SOURCES = [{ v: 'all', l: 'All sources' }, { v: 'upload', l: 'Upload' }];
const SORTS = [
  { v: 'createdAt', l: 'Date added' }, { v: 'name', l: 'Name' }, { v: 'size', l: 'Size' },
];
const PREVIEWABLE_IFRAME = ['pdf'];
const PREVIEWABLE_IMG = ['png', 'jpg', 'jpeg', 'gif'];

function fileGlyph(t, size = 15) {
  const k = (t || '').toLowerCase();
  if (['png', 'jpg', 'jpeg', 'gif'].includes(k)) return <FileImage size={size} />;
  if (['xlsx', 'xls', 'csv'].includes(k)) return <FileSpreadsheet size={size} />;
  if (!TYPE_META[k]) return <FileIcon size={size} />;
  return <FileText size={size} />;
}
const fmtSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(b >= 10485760 ? 0 : 1)} MB` : b >= 1024 ? `${Math.round(b / 1024)} KB` : `${b} B`);
const fmtAgo = (iso) => {
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  if (m < 1440) return `${Math.round(m / 60)}h ago`;
  const d = Math.round(m / 1440);
  return d < 30 ? `${d}d ago` : `${Math.round(d / 30)}mo ago`;
};
const fmtDate = (iso) => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });

// ── chips ──
const TypeChip = ({ t }) => {
  const m = TYPE_META[(t || '').toLowerCase()] || { bg: T.head, text: T.ink2, dot: T.ink4, label: (t || 'FILE').toUpperCase() };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700, letterSpacing: '.02em', background: m.bg, color: m.text }}>
      <span style={{ width: 5, height: 5, borderRadius: 99, background: m.dot }} />{m.label}
    </span>
  );
};
const FolderChip = () => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700, letterSpacing: '.02em', background: T.head, color: T.ink3, border: `1px solid ${T.border}` }}>FOLDER</span>
);
const SourceChip = () => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '2px 8px', borderRadius: 6, fontSize: 11.5, fontWeight: 600, background: T.head, color: T.ink2 }}>Upload</span>
);
const AccessChip = () => (
  <span style={{ display: 'inline-flex', alignItems: 'center', padding: '1px 7px', borderRadius: 5, fontSize: 10.5, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', background: '#fff', color: T.ink3, border: `1px solid ${T.border}` }}>Private</span>
);

// ── primitives ──
const dlPill = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 12, fontSize: 13, fontWeight: 500, background: '#fff', border: `1px solid ${T.border}`, color: T.ink2, whiteSpace: 'nowrap' };
const dlBtnDark = { display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderRadius: 12, fontSize: 13, fontWeight: 700, background: T.black, color: T.yellow, border: 'none', cursor: 'pointer' };

function DLIconBtn({ children, title, onClick, tone }) {
  const [h, setH] = useState(false);
  return (
    <button title={title} onClick={(e) => { e.stopPropagation(); onClick?.(); }} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{ height: 30, width: 30, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8, border: 'none', cursor: 'pointer', color: h && tone === 'danger' ? '#8a1020' : T.ink2, background: h ? (tone === 'danger' ? '#fce8eb' : '#fff') : 'transparent', boxShadow: h && tone !== 'danger' ? '0 1px 2px rgba(42,42,42,.08)' : 'none', transition: 'all 120ms' }}>
      {children}
    </button>
  );
}
function DLGhost({ children, onClick, icon, tone }) {
  const [h, setH] = useState(false);
  return (
    <button onClick={onClick} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 12px', borderRadius: 12, fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', border: 'none', color: tone === 'danger' ? '#8a1020' : T.ink, background: h ? (tone === 'danger' ? '#fce8eb' : T.hover) : 'transparent', transition: 'background 120ms' }}>
      {icon}{children}
    </button>
  );
}
function Check({ on, onClick, dim }) {
  return (
    <button onClick={(e) => { e.stopPropagation(); onClick(); }}
      style={{ height: 17, width: 17, borderRadius: 5, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0, border: `1.5px solid ${on ? T.black : T.ink4}`, background: on ? T.black : '#fff', color: T.yellow, opacity: dim && !on ? 0.35 : 1, transition: 'all 120ms' }}>
      {on && <CheckIcon size={11} strokeWidth={3} />}
    </button>
  );
}
function DLFilter({ label, value, onChange, options, icon }) {
  return (
    <div style={dlPill}>
      {icon}<span style={{ fontSize: 12, color: T.ink3 }}>{label}:</span>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-auto w-auto gap-1 border-0 bg-transparent p-0 text-[13px] font-semibold shadow-none focus:ring-0 focus:ring-offset-0 [&>svg]:h-3 [&>svg]:w-3 [&>svg]:opacity-100" style={{ color: T.ink, fontFamily: 'inherit' }}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>{options.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}
function TextField({ label, value, onChange, placeholder, autoFocus, hint }) {
  const [f, setF] = useState(false);
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: T.ink3 }}>{label}</span>
      <input value={value} autoFocus={autoFocus} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        onFocus={() => setF(true)} onBlur={() => setF(false)}
        style={{ padding: '10px 12px', borderRadius: 11, fontSize: 13.5, fontFamily: 'inherit', color: T.ink, background: '#fff', outline: 'none', border: `1px solid ${f ? T.focus : T.border}`, boxShadow: f ? '0 0 0 4px rgba(255,216,122,.25)' : 'none', transition: 'all 120ms' }} />
      {hint && <span style={{ fontSize: 11.5, color: T.ink3 }}>{hint}</span>}
    </label>
  );
}
function Modal({ title, onClose, children, footer, width = 440, icon }) {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(42,42,42,.28)' }} />
      <div style={{ position: 'relative', width, maxHeight: '82%', display: 'flex', flexDirection: 'column', background: '#fff', border: `1px solid ${T.border}`, borderRadius: 16, boxShadow: '0 24px 70px rgba(42,42,42,.22)', overflow: 'hidden' }}>
        <header style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '16px 18px', borderBottom: `1px solid ${T.border}`, background: T.page }}>
          {icon && <span style={{ height: 30, width: 30, borderRadius: 9, background: '#fff', border: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.ink2 }}>{icon}</span>}
          <h3 style={{ margin: 0, flex: 1, fontSize: 15.5, fontWeight: 700, letterSpacing: '-.01em', color: T.ink }}>{title}</h3>
          <DLIconBtn title="Close" onClick={onClose}><X size={15} /></DLIconBtn>
        </header>
        <div style={{ padding: 18, overflow: 'auto', flex: 1 }}>{children}</div>
        {footer && <footer style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, padding: '12px 18px', borderTop: `1px solid ${T.border}`, background: T.page }}>{footer}</footer>}
      </div>
    </div>
  );
}

const th = (x) => ({ padding: '14px 18px', textAlign: 'left', fontSize: 11.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em', color: '#8c8779', background: '#f5f3e9', borderBottom: '1px solid #ece8d8', whiteSpace: 'nowrap', ...x });
const td = (x) => ({ padding: '14px 18px', fontSize: 13, color: T.ink2, verticalAlign: 'middle', ...x });

// ── folder tree sidebar (lazy-loaded) ──
function FolderTree({ rootFolders, rootCount, cache, loadingIds, currentId, onOpen, onExpand }) {
  const [open, setOpen] = useState(() => new Set());
  const Row = ({ node, depth }) => {
    const [h, setH] = useState(false);
    const cached = cache[node.id];
    const kids = cached?.folders ?? [];
    const knownEmpty = !!cached && kids.length === 0;
    const isOpen = open.has(node.id);
    const cur = currentId === node.id;
    const count = cached ? cached.folders.length + cached.files.length : null;
    return (
      <>
        <div onClick={() => onOpen(node.id)} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: `6px 10px 6px ${10 + depth * 14}px`, borderRadius: 9, cursor: 'pointer', background: cur ? T.head : h ? T.hover : 'transparent', transition: 'background 100ms' }}>
          <span onClick={(e) => { e.stopPropagation(); if (knownEmpty) return; if (!cached) onExpand(node.id); setOpen((s) => { const n = new Set(s); n.has(node.id) ? n.delete(node.id) : n.add(node.id); return n; }); }}
            style={{ width: 14, display: 'flex', justifyContent: 'center', color: T.ink4, visibility: knownEmpty ? 'hidden' : 'visible', transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform 120ms' }}>
            <ChevronRight size={12} />
          </span>
          <span style={{ color: cur ? T.ink : T.ink3, display: 'flex' }}><FolderIcon size={14} /></span>
          <span style={{ flex: 1, fontSize: 12.5, fontWeight: cur ? 600 : 500, color: cur ? T.ink : T.ink2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{node.name}</span>
          {count !== null && <span style={{ fontSize: 11, fontFamily: 'monospace', color: T.ink4 }}>{count}</span>}
        </div>
        {isOpen && kids.map((k) => <Row key={k.id} node={k} depth={depth + 1} />)}
      </>
    );
  };
  const [rootH, setRootH] = useState(false);
  return (
    <aside style={{ width: 232, flexShrink: 0, borderRight: `1px solid ${T.border}`, padding: '12px 10px', display: 'flex', flexDirection: 'column', gap: 2, background: '#fdfdf7' }}>
      <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', color: T.ink3, padding: '2px 10px 8px' }}>Folders</span>
      <div onClick={() => onOpen(null)} onMouseEnter={() => setRootH(true)} onMouseLeave={() => setRootH(false)}
        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 9, cursor: 'pointer', background: currentId === null ? T.head : rootH ? T.hover : 'transparent' }}>
        <HardDrive size={14} />
        <span style={{ flex: 1, fontSize: 12.5, fontWeight: currentId === null ? 600 : 500, color: T.ink }}>All documents</span>
        <span style={{ fontSize: 11, fontFamily: 'monospace', color: T.ink4 }}>{rootCount}</span>
      </div>
      <div style={{ height: 1, background: T.border, margin: '8px 6px' }} />
      {rootFolders.map((n) => <Row key={n.id} node={n} depth={0} />)}
    </aside>
  );
}

export default function DocsPage() {
  const { user } = useAuth();
  const foId = user?.family_office_id || '';

  const [folderId, setFolderId] = useState(null);
  const [breadcrumb, setBreadcrumb] = useState([{ id: null, name: 'All documents' }]);
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);

  const [folderCache, setFolderCache] = useState({});
  const [loadingIds] = useState(new Set());

  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [srcFilter, setSrcFilter] = useState('all');
  const [sortKey, setSortKey] = useState('createdAt');
  const [dir, setDir] = useState('desc');
  const [view, setView] = useState('list');
  const [hover, setHover] = useState(null);
  const [sel, setSel] = useState(new Set());

  const [dialog, setDialog] = useState(null); // { kind: 'newFolder'|'rename'|'delete', ... }
  const [busy, setBusy] = useState(false);

  const [previewFile, setPreviewFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);

  const load = useCallback(async (fid) => {
    if (!foId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/docs/browse`, { params: { family_office_id: foId, folder_id: fid } });
      setFolders(res.data.folders || []);
      setFiles(res.data.files || []);
      setBreadcrumb(res.data.breadcrumb || [{ id: null, name: 'All documents' }]);
      setFolderCache((c) => ({ ...c, [fid === null ? '__root__' : fid]: { folders: res.data.folders || [], files: res.data.files || [] } }));
    } catch {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [foId]);

  useEffect(() => { load(folderId); setSel(new Set()); }, [folderId, load]);

  const rootFolders = folderCache['__root__']?.folders || (folderId === null ? folders : []);
  const rootCount = (folderCache['__root__']?.folders.length || 0) + (folderCache['__root__']?.files.length || 0);

  const expandTreeNode = async (id) => {
    if (!foId || folderCache[id]) return;
    try {
      const res = await axios.get(`${API}/docs/browse`, { params: { family_office_id: foId, folder_id: id } });
      setFolderCache((c) => ({ ...c, [id]: { folders: res.data.folders || [], files: res.data.files || [] } }));
    } catch { /* ignore */ }
  };

  // ── rows: combine, filter, sort ──
  const rows = useMemo(() => {
    let combined = [
      ...folders.map((f) => ({ kind: 'folder', node: f })),
      ...files.map((f) => ({ kind: 'file', node: f })),
    ];
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      combined = combined.filter((r) => r.node.name.toLowerCase().includes(q));
    }
    if (typeFilter === 'folder') combined = combined.filter((r) => r.kind === 'folder');
    else if (typeFilter !== 'all') combined = combined.filter((r) => r.kind === 'file' && (r.node.file_type || '').toLowerCase() === typeFilter);
    combined.sort((a, b) => {
      let av, bv;
      if (sortKey === 'name') { av = a.node.name.toLowerCase(); bv = b.node.name.toLowerCase(); }
      else if (sortKey === 'size') { av = a.kind === 'folder' ? -1 : a.node.file_size; bv = b.kind === 'folder' ? -1 : b.node.file_size; }
      else { av = a.node.created_at; bv = b.node.created_at; }
      if (av < bv) return dir === 'asc' ? -1 : 1;
      if (av > bv) return dir === 'asc' ? 1 : -1;
      return 0;
    });
    return combined;
  }, [folders, files, query, typeFilter, sortKey, dir]);

  const isEmpty = rows.length === 0;
  const totalFiles = files.length;
  const totalFolders = folders.length;
  const totalSize = files.reduce((s, f) => s + (f.file_size || 0), 0);

  const allOn = rows.length > 0 && rows.every((r) => sel.has(r.node.id));
  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSel(allOn ? new Set() : new Set(rows.map((r) => r.node.id)));

  // ── actions ──
  const handleCreateFolder = async (name) => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/docs/folders`, { family_office_id: foId, name: name.trim(), parent_id: folderId });
      toast.success('Folder created');
      setDialog(null);
      setFolderCache((c) => { const n = { ...c }; delete n[folderId === null ? '__root__' : folderId]; return n; });
      load(folderId);
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || 'Failed to create folder');
    } finally { setBusy(false); }
  };

  const handleRename = async (kind, id, name) => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const path = kind === 'folder' ? 'folders' : 'files';
      await axios.patch(`${API}/docs/${path}/${id}`, { name: name.trim() }, { params: { family_office_id: foId } });
      toast.success('Renamed');
      setDialog(null);
      load(folderId);
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || 'Rename failed');
    } finally { setBusy(false); }
  };

  const handleDelete = async (kind, id) => {
    setBusy(true);
    try {
      const ids = String(id).split(',');
      for (const oneId of ids) {
        const path = kind === 'folder' ? 'folders' : 'files';
        await axios.delete(`${API}/docs/${path}/${oneId}`, { params: { family_office_id: foId } });
      }
      toast.success('Deleted');
      setDialog(null);
      setSel(new Set());
      load(folderId);
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || 'Delete failed');
    } finally { setBusy(false); }
  };

  const doUpload = async (fileList) => {
    const chosen = Array.from(fileList || []);
    if (chosen.length === 0) return;
    setUploading(true);
    try {
      for (const file of chosen) {
        const form = new FormData();
        form.append('family_office_id', foId);
        if (folderId) form.append('folder_id', folderId);
        form.append('file', file);
        await axios.post(`${API}/docs/files/upload`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      toast.success(chosen.length > 1 ? `${chosen.length} files uploaded` : 'File uploaded');
      setDialog(null);
      load(folderId);
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || 'Upload failed');
    } finally { setUploading(false); }
  };

  const onDrag = (e) => { e.preventDefault(); e.stopPropagation(); setDrag(e.type === 'dragenter' || e.type === 'dragover'); };
  const onDrop = (e) => { e.preventDefault(); e.stopPropagation(); setDrag(false); doUpload(e.dataTransfer.files); };

  const openPreview = async (file) => {
    setPreviewFile(file);
    setPreviewUrl(null);
    setPreviewLoading(true);
    try {
      const res = await axios.get(`${API}/docs/files/${file.id}/url`, { params: { family_office_id: foId } });
      setPreviewUrl(res.data.url);
    } catch {
      toast.error('Failed to load preview');
    } finally { setPreviewLoading(false); }
  };

  const handleDownload = async (file) => {
    try {
      const res = await axios.get(`${API}/docs/files/${file.id}/url`, { params: { family_office_id: foId, download: true } });
      window.open(res.data.url, '_blank');
    } catch { toast.error('Failed to download'); }
  };

  const downloadSelected = async () => {
    const targets = rows.filter((r) => r.kind === 'file' && sel.has(r.node.id)).map((r) => r.node);
    for (const f of targets) await handleDownload(f);
  };

  const SortHead = ({ label, k }) => (
    <span onClick={() => (sortKey === k ? setDir((d) => (d === 'asc' ? 'desc' : 'asc')) : setSortKey(k))}
      style={{ cursor: 'pointer', userSelect: 'none', display: 'inline-flex', alignItems: 'center', gap: 4, color: sortKey === k ? T.ink : T.ink3 }}>
      {label}{sortKey === k ? (dir === 'asc' ? <ArrowUp size={11} /> : <ArrowDown size={11} />) : <ArrowUpDown size={11} />}
    </span>
  );

  const rowActions = (kind, node) => (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 2, opacity: hover === node.id ? 1 : 0, transition: 'opacity 120ms' }}>
      {kind === 'file' && <DLIconBtn title="Download" onClick={() => handleDownload(node)}><Download size={14} /></DLIconBtn>}
      <DLIconBtn title="Rename" onClick={() => setDialog({ kind: 'rename', type: kind, id: node.id, name: node.name })}><Pencil size={14} /></DLIconBtn>
      <DLIconBtn title="Delete" tone="danger" onClick={() => setDialog({ kind: 'delete', type: kind, id: node.id, name: node.name })}><Trash2 size={14} /></DLIconBtn>
    </div>
  );

  const GridCard = ({ kind, node }) => {
    const [h, setH] = useState(false);
    const isF = kind === 'folder';
    const file = node;
    const on = sel.has(node.id);
    return (
      <div onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)} onClick={() => (isF ? setFolderId(node.id) : openPreview(file))}
        style={{ border: `1px solid ${on ? T.focus : T.border}`, borderRadius: 12, background: '#fff', cursor: 'pointer', overflow: 'hidden', boxShadow: h ? '0 4px 14px rgba(42,42,42,.08)' : '0 1px 2px rgba(42,42,42,.04)', transition: 'all 130ms' }}>
        <div style={{ height: 104, background: isF ? T.head : stripes, borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', color: T.ink3 }}>
          <div style={{ position: 'absolute', top: 8, left: 8, opacity: on || h ? 1 : 0, transition: 'opacity 120ms' }}>
            <Check on={on} onClick={() => toggle(node.id)} />
          </div>
          {isF ? <FolderIcon size={30} strokeWidth={1.4} /> : <span style={{ fontFamily: 'monospace', fontSize: 10.5, color: T.ink3, background: '#fff', border: `1px solid ${T.border}`, borderRadius: 6, padding: '3px 7px' }}>{(file.file_type || 'file').toUpperCase()}</span>}
        </div>
        <div style={{ padding: '10px 12px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: 12.5, fontWeight: 600, color: T.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{node.name}</span>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <span style={{ fontSize: 11, color: T.ink3 }}>{isF ? 'Folder' : fmtSize(file.file_size)}</span>
            <span style={{ fontSize: 11, color: T.ink4 }}>{fmtAgo(node.created_at)}</span>
          </div>
        </div>
      </div>
    );
  };

  const currentFolder = breadcrumb.length > 1 ? breadcrumb[breadcrumb.length - 1] : null;
  const parentId = breadcrumb.length > 2 ? breadcrumb[breadcrumb.length - 2].id : null;

  return (
    <div style={{ position: 'relative', background: T.page, minHeight: '100vh', color: T.ink }} data-testid="docs-page">
      <div style={{ padding: '22px 32px 34px' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 24, marginBottom: 20, flexWrap: 'wrap' }}>
          <h1 style={{ margin: 0, display: 'flex', alignItems: 'baseline', gap: 12, fontSize: 28, fontWeight: 700, letterSpacing: '-0.025em', lineHeight: 1, color: '#000' }}>
            Document Library
            <span style={{ fontSize: 14, fontWeight: 500, color: T.ink3, letterSpacing: 0, whiteSpace: 'nowrap' }}>
              {totalFiles} file{totalFiles === 1 ? '' : 's'} · {totalFolders} folder{totalFolders === 1 ? '' : 's'}
            </span>
          </h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button onClick={() => toast('No external sources connected yet')} style={{ ...dlPill, height: 42, padding: '0 14px', fontWeight: 600, cursor: 'pointer' }}><Cloud size={14} />Sources</button>
            <button onClick={() => setDialog({ kind: 'newFolder' })} style={{ ...dlPill, height: 42, padding: '0 14px', fontWeight: 600, cursor: 'pointer' }}><Plus size={14} strokeWidth={2.2} />New folder</button>
            <button onClick={() => fileInputRef.current?.click()} disabled={uploading} style={{ ...dlBtnDark, height: 42, padding: '0 16px', background: T.focus, color: T.ink }}>
              {uploading ? <Loader2 size={13.5} className="animate-spin" /> : <UploadIcon size={13.5} strokeWidth={2.3} />}Upload files
            </button>
            <input ref={fileInputRef} type="file" multiple className="hidden" style={{ display: 'none' }} onChange={(e) => { doUpload(e.target.files); e.target.value = ''; }} />
          </div>
        </div>

        {/* Toolbar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', flex: '0 1 360px' }}>
            <span style={{ position: 'absolute', left: 12, display: 'flex', color: T.ink3, pointerEvents: 'none' }}><Search size={14} /></span>
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search this folder…"
              style={{ width: '100%', borderRadius: 12, fontSize: 13, outline: 'none', background: '#fff', border: `1px solid ${T.border}`, padding: '10px 32px 10px 34px', color: T.ink, fontFamily: 'inherit' }} />
            {query && <button onClick={() => setQuery('')} style={{ position: 'absolute', right: 8, height: 20, width: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 99, border: 'none', background: 'transparent', color: T.ink3, cursor: 'pointer' }}><X size={13} /></button>}
          </div>
          <DLFilter label="Type" value={typeFilter} onChange={setTypeFilter} options={TYPES} icon={<Tag size={13} />} />
          <DLFilter label="Source" value={srcFilter} onChange={setSrcFilter} options={SOURCES} icon={<Cloud size={13} />} />
          <div style={{ flex: 1 }} />
          <div style={dlPill}>
            <span style={{ fontSize: 12, color: T.ink3 }}>Sort:</span>
            <Select value={sortKey} onValueChange={setSortKey}>
              <SelectTrigger className="h-auto w-auto gap-1 border-0 bg-transparent p-0 text-[13px] font-semibold shadow-none focus:ring-0 focus:ring-offset-0 [&>svg]:h-3 [&>svg]:w-3 [&>svg]:opacity-100" style={{ color: T.ink, fontFamily: 'inherit' }}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>{SORTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
            </Select>
            <button onClick={() => setDir((d) => (d === 'asc' ? 'desc' : 'asc'))} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: T.ink2, display: 'flex', padding: 0 }}>{dir === 'asc' ? <ArrowUp size={13} /> : <ArrowDown size={13} />}</button>
          </div>
          <div style={{ display: 'flex', padding: 3, gap: 2, background: '#fff', border: `1px solid ${T.border}`, borderRadius: 12 }}>
            {[['list', <Rows3 key="l" size={14} />], ['grid', <LayoutGrid key="g" size={14} />]].map(([v, ic]) => (
              <button key={v} onClick={() => setView(v)} title={v === 'list' ? 'List view' : 'Grid view'}
                style={{ height: 28, width: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 9, border: 'none', cursor: 'pointer', background: view === v ? T.black : 'transparent', color: view === v ? T.yellow : T.ink2, transition: 'all 120ms' }}>{ic}</button>
            ))}
          </div>
        </div>

        {/* Content card */}
        <div onDragEnter={onDrag} onDragOver={onDrag} onDragLeave={onDrag} onDrop={onDrop}
          style={{ position: 'relative', background: '#fff', border: `1px solid ${drag ? T.focus : T.border}`, borderRadius: 14, boxShadow: drag ? '0 0 0 4px rgba(255,216,122,.25)' : '0 1px 2px rgba(42,42,42,.04)', overflow: 'hidden', display: 'flex', minHeight: 560, transition: 'all 130ms' }}>
          <FolderTree rootFolders={rootFolders} rootCount={rootCount} cache={folderCache} loadingIds={loadingIds} currentId={folderId} onOpen={setFolderId} onExpand={expandTreeNode} />

          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            {/* Breadcrumb / selection bar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 16px', borderBottom: `1px solid ${T.border}`, background: sel.size ? '#fffaea' : T.page, minHeight: 52, flexWrap: 'wrap' }}>
              {sel.size > 0 ? (
                <>
                  <Check on onClick={() => setSel(new Set())} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: T.ink }}>{sel.size} selected</span>
                  <div style={{ width: 1, height: 20, background: T.border, margin: '0 4px' }} />
                  <DLGhost icon={<Download size={13} />} onClick={downloadSelected}>Download</DLGhost>
                  <DLGhost icon={<Trash2 size={13} />} tone="danger" onClick={() => {
                    const first = rows.find((r) => sel.has(r.node.id));
                    if (first) setDialog({ kind: 'delete', type: sel.size === 1 ? first.kind : 'file', id: [...sel].join(','), name: `${sel.size} item${sel.size > 1 ? 's' : ''}` });
                  }}>Delete</DLGhost>
                  <div style={{ flex: 1 }} />
                  <button onClick={() => setSel(new Set())} style={{ ...dlPill, padding: '6px 11px', fontSize: 12.5, cursor: 'pointer' }}>Clear</button>
                </>
              ) : (
                <>
                  {currentFolder && <DLIconBtn title="Up one level" onClick={() => setFolderId(parentId)}><ChevronLeft size={15} /></DLIconBtn>}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
                    {breadcrumb.map((b, i) => (
                      <React.Fragment key={b.id ?? 'root'}>
                        {i > 0 && <span style={{ color: T.ink4, display: 'flex' }}><ChevronRight size={12} /></span>}
                        <span onClick={() => setFolderId(b.id)} style={{ display: i === 0 ? 'inline-flex' : undefined, alignItems: 'center', gap: 6, fontSize: 13, color: i === breadcrumb.length - 1 ? T.ink : T.ink3, fontWeight: i === breadcrumb.length - 1 ? 600 : 500, cursor: 'pointer' }}>
                          {i === 0 && <HardDrive size={13} />}{b.name}
                        </span>
                      </React.Fragment>
                    ))}
                  </div>
                  <span style={{ fontSize: 11.5, color: T.ink3, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{totalFolders} folders · {totalFiles} files · {fmtSize(totalSize)}</span>
                </>
              )}
            </div>

            {loading ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div className="animate-spin rounded-full h-10 w-10 border-b-2" style={{ borderColor: T.ink }} />
              </div>
            ) : isEmpty ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '56px 24px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, textAlign: 'center', maxWidth: 400 }}>
                  <div style={{ height: 40, width: 40, borderRadius: 12, background: T.head, display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.ink3 }}>{query ? <Search size={17} /> : <FolderIcon size={18} />}</div>
                  <div>
                    <p style={{ margin: '0 0 4px', fontWeight: 600, fontSize: 15.5, color: T.ink }}>{query ? `Nothing matches "${query}"` : 'This folder is empty'}</p>
                    <p style={{ margin: 0, fontSize: 13, color: T.ink3 }}>{query ? 'Try a shorter term, or clear the type filter.' : 'Drop files anywhere on this panel, or create a subfolder to organise them.'}</p>
                  </div>
                  {!query && (
                    <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
                      <button onClick={() => fileInputRef.current?.click()} style={{ ...dlBtnDark, background: T.focus, color: T.ink }}><UploadIcon size={13} strokeWidth={2.3} />Upload files</button>
                      <button onClick={() => setDialog({ kind: 'newFolder' })} style={{ ...dlPill, padding: '10px 14px', fontWeight: 600, cursor: 'pointer' }}><Plus size={13} strokeWidth={2.2} />New folder</button>
                    </div>
                  )}
                </div>
              </div>
            ) : view === 'grid' ? (
              <div style={{ padding: 16, display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, alignContent: 'start', overflow: 'auto' }}>
                {rows.map(({ kind, node }) => <GridCard key={node.id} kind={kind} node={node} />)}
              </div>
            ) : (
              <div style={{ overflow: 'auto', flex: 1 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                  <colgroup><col style={{ width: 44 }} /><col /><col style={{ width: 104 }} /><col style={{ width: 92 }} /><col style={{ width: 110 }} /><col style={{ width: 96 }} /><col style={{ width: 150 }} /><col style={{ width: 118 }} /></colgroup>
                  <thead><tr style={{ background: T.head, borderBottom: `1px solid ${T.border}` }}>
                    <th style={th({ padding: '11px 0 11px 16px' })}><Check on={allOn} dim onClick={toggleAll} /></th>
                    <th style={th()}><SortHead label="Name" k="name" /></th>
                    <th style={th()}>Type</th>
                    <th style={th()}><SortHead label="Size" k="size" /></th>
                    <th style={th()}>Source</th>
                    <th style={th()}>Access</th>
                    <th style={th()}><SortHead label="Added" k="createdAt" /></th>
                    <th style={th()} />
                  </tr></thead>
                  <tbody>
                    {rows.map(({ kind, node }) => {
                      const h = hover === node.id, isF = kind === 'folder', on = sel.has(node.id);
                      const file = node;
                      return (
                        <tr key={node.id} onMouseEnter={() => setHover(node.id)} onMouseLeave={() => setHover(null)} onClick={() => (isF ? setFolderId(node.id) : openPreview(file))}
                          style={{ borderBottom: `1px solid ${T.border}`, cursor: 'pointer', background: on ? '#fffaea' : h ? T.hover : 'transparent', transition: 'background 100ms' }}>
                          <td style={td({ padding: '11px 0 11px 16px' })}><div style={{ opacity: on || h ? 1 : 0.32, transition: 'opacity 120ms' }}><Check on={on} onClick={() => toggle(node.id)} /></div></td>
                          <td style={td({ maxWidth: 0 })} title={node.name}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                              <span style={{ height: 30, width: 30, borderRadius: 9, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: isF ? T.head : '#fff', border: `1px solid ${T.border}`, color: isF ? T.ink : T.ink3 }}>
                                {isF ? <FolderIcon size={16} /> : fileGlyph(file.file_type, 15)}
                              </span>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
                                <span style={{ fontSize: 13.5, fontWeight: 600, color: T.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{node.name}</span>
                                <span style={{ fontSize: 11, color: T.ink3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{isF ? 'Folder' : fmtSize(file.file_size)}</span>
                              </div>
                            </div>
                          </td>
                          <td style={td()}>{isF ? <FolderChip /> : <TypeChip t={file.file_type} />}</td>
                          <td style={td({ fontFamily: 'monospace', fontSize: 12, color: isF ? T.ink4 : T.ink2, fontVariantNumeric: 'tabular-nums' })}>{isF ? '—' : fmtSize(file.file_size)}</td>
                          <td style={td()}><SourceChip /></td>
                          <td style={td()}><AccessChip /></td>
                          <td style={td()}>
                            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
                              <span style={{ fontSize: 12.5, color: T.ink }}>{fmtDate(node.created_at)}</span>
                              <span style={{ fontFamily: 'monospace', fontSize: 11, color: T.ink3 }}>{fmtAgo(node.created_at)}</span>
                            </div>
                          </td>
                          <td style={td({ textAlign: 'right' })} onClick={(e) => e.stopPropagation()}>{rowActions(kind, node)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {drag && (
              <div style={{ position: 'absolute', inset: 0, zIndex: 20, background: 'rgba(251,251,243,.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '26px 34px', borderRadius: 16, border: `1.5px dashed ${T.focus}`, background: '#fff', boxShadow: '0 12px 40px rgba(42,42,42,.12)' }}>
                  <UploadIcon size={22} strokeWidth={2} />
                  <span style={{ fontSize: 14.5, fontWeight: 700, color: T.ink }}>Drop to upload here</span>
                  <span style={{ fontSize: 12.5, color: T.ink3 }}>Files land in {currentFolder ? currentFolder.name : 'All documents'}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Preview */}
      {previewFile && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div onClick={() => setPreviewFile(null)} style={{ position: 'absolute', inset: 0, background: 'rgba(42,42,42,.5)' }} />
          <div style={{ position: 'relative', width: 'min(94vw, 1280px)', height: 'min(92vh, 900px)', background: '#fff', borderRadius: 16, border: `1px solid ${T.border}`, boxShadow: '0 24px 80px rgba(42,42,42,.28)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <header style={{ padding: '16px 22px 14px', borderBottom: `1px solid ${T.border}`, background: T.page, flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 9 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <TypeChip t={previewFile.file_type} /><SourceChip /><AccessChip />
                </div>
                <DLIconBtn title="Close" onClick={() => setPreviewFile(null)}><X size={15} /></DLIconBtn>
              </div>
              <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, letterSpacing: '-.01em', color: T.ink, lineHeight: 1.3, wordBreak: 'break-word' }}>{previewFile.name}</h2>
              <div style={{ marginTop: 7, display: 'flex', alignItems: 'center', gap: 8, fontSize: 11.5, color: T.ink3, fontFamily: 'monospace' }}>
                <span>{fmtSize(previewFile.file_size)}</span>
              </div>
            </header>
            <div style={{ flex: 1, minHeight: 0, background: T.head, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'auto' }}>
              {previewLoading || !previewUrl ? (
                <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: T.ink3 }} />
              ) : PREVIEWABLE_IMG.includes((previewFile.file_type || '').toLowerCase()) ? (
                <img src={previewUrl} alt={previewFile.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
              ) : PREVIEWABLE_IFRAME.includes((previewFile.file_type || '').toLowerCase()) ? (
                <iframe src={previewUrl} title={previewFile.name} style={{ width: '100%', height: '100%', border: 0 }} />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 9 }}>
                  <span style={{ color: T.ink3, display: 'flex' }}>{fileGlyph(previewFile.file_type, 26)}</span>
                  <span style={{ fontFamily: 'monospace', fontSize: 11, color: T.ink3, background: '#fff', border: `1px solid ${T.border}`, borderRadius: 7, padding: '4px 9px' }}>No inline preview for this file type</span>
                  <DLGhost icon={<Download size={13} />} onClick={() => handleDownload(previewFile)}>Download instead</DLGhost>
                </div>
              )}
            </div>
            <footer style={{ padding: '12px 22px', borderTop: `1px solid ${T.border}`, background: T.page, display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              <button onClick={() => handleDownload(previewFile)} style={dlBtnDark}><Download size={13.5} strokeWidth={2.3} />Download</button>
              <DLGhost icon={<Pencil size={13} />} onClick={() => setDialog({ kind: 'rename', type: 'file', id: previewFile.id, name: previewFile.name })}>Rename</DLGhost>
              <span style={{ fontSize: 11.5, color: T.ink3, fontFamily: 'monospace' }}>{fmtSize(previewFile.file_size)} · Uploaded {fmtDate(previewFile.created_at)} ({fmtAgo(previewFile.created_at)})</span>
            </footer>
          </div>
        </div>
      )}

      {/* New folder */}
      {dialog?.kind === 'newFolder' && (
        <NewFolderDialog onClose={() => setDialog(null)} onCreate={handleCreateFolder} where={currentFolder ? currentFolder.name : 'All documents'} busy={busy} />
      )}
      {/* Rename */}
      {dialog?.kind === 'rename' && (
        <RenameDialog kind={dialog.type} name={dialog.name} busy={busy} onClose={() => setDialog(null)} onRename={(name) => handleRename(dialog.type, dialog.id, name)} />
      )}
      {/* Delete confirm */}
      {dialog?.kind === 'delete' && (
        <Modal title={`Delete ${dialog.type === 'folder' ? 'folder' : 'file'}?`} onClose={() => setDialog(null)} width={420}
          footer={<>
            <button onClick={() => setDialog(null)} style={{ ...dlPill, padding: '9px 14px', cursor: 'pointer' }}>Cancel</button>
            <button onClick={() => handleDelete(dialog.type, dialog.id)} disabled={busy} style={{ ...dlBtnDark, background: '#e31837', color: '#fff' }}>{busy ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}Delete</button>
          </>}>
          <p style={{ margin: 0, fontSize: 13.5, color: T.ink2 }}>
            Delete <b style={{ color: T.ink }}>{dialog.name}</b>{dialog.type === 'folder' ? ' and everything inside it' : ''}? This can't be undone.
          </p>
        </Modal>
      )}
    </div>
  );
}

function NewFolderDialog({ onClose, onCreate, where, busy }) {
  const [name, setName] = useState('');
  return (
    <Modal title="New Folder" onClose={onClose} icon={<FolderIcon size={15} />}
      footer={<>
        <button onClick={onClose} style={{ ...dlPill, padding: '9px 14px', cursor: 'pointer' }}>Cancel</button>
        <button onClick={() => onCreate(name)} disabled={busy || !name.trim()} style={{ ...dlBtnDark, background: T.focus, color: T.ink, opacity: !name.trim() ? 0.5 : 1 }}>{busy ? <Loader2 size={13} className="animate-spin" /> : 'Create'}</button>
      </>}>
      <TextField label="Folder name" value={name} onChange={setName} placeholder="e.g. Board Decks" autoFocus hint={`Will be created in ${where}`} />
    </Modal>
  );
}

function RenameDialog({ kind, name: initial, onClose, onRename, busy }) {
  const [name, setName] = useState(initial);
  return (
    <Modal title={`Rename ${kind === 'folder' ? 'Folder' : 'File'}`} onClose={onClose} icon={<Pencil size={15} />}
      footer={<>
        <button onClick={onClose} style={{ ...dlPill, padding: '9px 14px', cursor: 'pointer' }}>Cancel</button>
        <button onClick={() => onRename(name)} disabled={busy || !name.trim()} style={{ ...dlBtnDark, background: T.focus, color: T.ink, opacity: !name.trim() ? 0.5 : 1 }}>{busy ? <Loader2 size={13} className="animate-spin" /> : 'Save'}</button>
      </>}>
      <TextField label="Name" value={name} onChange={setName} autoFocus />
    </Modal>
  );
}
